import * as React from 'react'
import styled from 'styled-components'
import theme from 'theme'
import SliderItem from './SliderItem'
import useArrowNavigation from 'hooks/useSliderArrowNavigation'
import SliderButton from 'theme/atoms/SliderButton'
import PositionIndicator from 'theme/molecules/ProductSlider/PositionIndicator'
import withTranslations from './withTranslations'
import useServiceReviews from './useServiceReviews'
import useTranslations from 'hooks/useTranslations'

function ServiceReviewSlider() {
  const t = useTranslations<'organisms-service-review-slider'>()
  const itemRef = React.useRef<null | HTMLDivElement>(null)
  const arrowNav = useArrowNavigation(() => {
    return itemRef.current?.clientWidth ?? 0
  })

  const reviews = useServiceReviews()
  const reviewsOverDefinedLength = React.useMemo(() => {
    if (reviews == null) return []
    return reviews
      .filter(
        (review) =>
          review.originalReview.comment &&
          review.originalReview.comment.length > 30
      )
      .splice(0, 15)
  }, [reviews])

  return (
    <Wrapper
      className="ServiceReviewSlider"
      data-cy-ctx="organisms/ServiceReviewSlider"
      $visible={reviewsOverDefinedLength.length > 0}
    >
      <div className="innerSlider">
        <div className="reviews" ref={arrowNav.productListRef}>
          <Indicator
            className="indicatorOne"
            ref={arrowNav.firstItemRef}
            $width={1}
          />
          {reviewsOverDefinedLength.map((review) => (
            <div ref={itemRef} className="slider-item" key={review.objectID}>
              <SliderItem key={review.objectID} {...review} />
            </div>
          ))}
          <Indicator
            className="indicatorTwo"
            ref={arrowNav.lastItemRef}
            $width={1}
          />
        </div>
        {!arrowNav.firstItemReached && (
          <SliderButton
            facing="left"
            title={t.asText(false, 'service_reviews_prev_button_title')}
            data-cy-handle="prev-button"
            data-cy-state="has-prev-button"
            onClick={() => arrowNav.handleSlide('left')}
          />
        )}
        {!arrowNav.lastItemReached && (
          <SliderButton
            facing="right"
            title={t.asText(false, 'service_reviews_next_button_title')}
            data-cy-handle="next-button"
            data-cy-state="has-next-button"
            onClick={() => arrowNav.handleSlide('right')}
          />
        )}
      </div>
      <PositionIndicator
        data-cy-state="show-position-indicator"
        componentRef={arrowNav.productListRef}
        maxSteps={reviewsOverDefinedLength.length}
        offset={1}
        displayedSteps={reviewsOverDefinedLength.length}
      />
    </Wrapper>
  )
}
export default withTranslations(ServiceReviewSlider)

const Wrapper = styled.div<{ $visible: boolean }>`
  ${(props) => !props.$visible && `display:none;`}
  flex-direction: column;
  width: 100%;
  max-width: var(--container-size);
  position: relative;
  @media (hover: hover) {
    &:hover > .innerSlider > .SliderButton {
      display: flex;
    }
  }
  > .innerSlider {
    display: flex;
    align-items: center;
    > .SliderButton {
      display: none;
      @media (min-width: ${theme.ms.LG}px) {
        display: flex;
      }
    }
    > .reviews {
      display: flex;
      width: 100%;
      max-width: 78.75rem;
      overflow-x: scroll;
      scrollbar-width: none;
      padding-bottom: ${theme.spacing('m')};
      ::-webkit-scrollbar {
        width: 0;
        background: transparent;
      }
    }
  }
`
const Indicator = styled.div<{ $width: number }>`
  width: ${(p) => p.$width + 'px'};
  min-width: ${(p) => p.$width + 'px'};
  align-self: center;
  height: 0.0625rem;
`
